import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, SlidersHorizontal } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { HelpTip } from "@/components/HelpTip";
import { adminColorLabel } from "@/lib/naming";
import { useFormats } from "@/lib/useFormats";
import { tileSize } from "@/lib/dims";

const UsName = ({ r }) => r.identity?.kind === "MOSAIC" ? null : r.us_color_name
  ? <span className="ml-2 text-xs font-semibold text-terracotta" data-testid={`inventory-us-name-${r.sku}`}>· {r.us_color_name}</span>
  : <span className="ml-2 badge-status badge-reserved" data-testid={`inventory-us-pending-${r.sku}`}>U.S. name pending</span>;

const LOCATION_BORDER = { MOROCCO: "#D8D5CE", IN_TRANSIT: "#B86F54", FLORIDA: "#96A58F" };
const LOCATION_LABEL = { MOROCCO: "Morocco", IN_TRANSIT: "In Transit", FLORIDA: "Florida" };

const REASONS = [
  "DAMAGE",
  "UNSALEABLE",
  "SAMPLE_USE",
  "WAREHOUSE_CORRECTION",
  "QUALITY_HOLD",
  "OTHER",
];

const FIELD_LABELS = {
  physical: "Physical (sellable)",
  reserved: "Reserved",
  committed: "Committed / Sold",
  sample_physical: "Sample pool physical",
  sample_reserved: "Sample pool reserved",
  damaged: "Damaged (not sellable)",
  quarantined: "Quarantined (not sellable)",
};

const REASON_LABELS = {
  DAMAGE: "Damage",
  UNSALEABLE: "Unsellable",
  SAMPLE_USE: "Sample use",
  WAREHOUSE_CORRECTION: "Warehouse correction",
  QUALITY_HOLD: "Quality hold",
  RECEIVING_DISCREPANCY: "Receiving discrepancy",
  CONTAINER_ALLOCATION: "Container allocation",
  MOROCCO_IMPORT: "Morocco import",
  RECEIVE: "Container receive",
  DAMAGE_RELEASE: "Damage released",
  QUARANTINE_RELEASE: "Quarantine released",
  WRITE_OFF: "Damaged write-off",
  RETURN_RESTOCK: "Return restocked",
  OTHER: "Other",
};

function InventoryTable({ rows, location, onAdjust, onSetMorocco }) {
  const readOnly = location === "IN_TRANSIT";
  return (
    <>
      {/* Mobile: one readable card per variant — identity + quantities together, no horizontal scroll */}
      <div className="space-y-3 md:hidden" data-testid={`inventory-cards-${location.toLowerCase()}`}>
        {rows.length === 0 ? (
          <p className="rounded-md border border-border bg-white py-12 text-center text-sm text-muted-foreground" data-testid={`inventory-cards-empty-${location.toLowerCase()}`}>No variants yet.</p>
        ) : (
          rows.map((r) => (
            <div key={r.variant_id} className="rounded-md border-2 bg-white p-4 shadow-[0_2px_12px_rgba(26,26,24,0.04)]" style={{ borderColor: LOCATION_BORDER[location] }} data-testid={`inventory-card-${r.sku}`}>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-warmgrey">{LOCATION_LABEL[location]}</p>
              <p className="font-medium text-charcoal">{r.label}</p>
              <p className="mt-0.5 text-xs text-charcoal" data-testid={`inventory-card-color-${r.sku}`}>{adminColorLabel(r)}</p>
              <p className="mt-0.5 font-mono text-xs text-muted-foreground">{r.sku} · {tileSize(r) || r.format_in}{r.mesh_sheet_in ? ` · mesh ${r.mesh_sheet_in}` : ""} · {r.box_coverage_sqft} sqft/box</p>
              <p className="num mt-2 text-lg font-semibold text-charcoal" data-testid={`card-avail-sqft-${r.sku}`}>
                {(Math.max(0, r.available * (r.box_coverage_sqft || 0))).toLocaleString(undefined, { maximumFractionDigits: 0 })} sq ft available
                <span className="ml-2 text-xs font-normal text-muted-foreground">{r.available} boxes</span>
              </p>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div><p className="text-sm text-charcoal" data-testid={`card-physical-${r.sku}`}>{r.physical}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Physical</p></div>
                <div><p className="text-sm text-[#8a6435]">{r.reserved}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Reserved</p></div>
                <div><p className="text-sm text-status-sold">{r.committed}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Committed</p></div>
                <div><span className={`badge-status ${r.available > 0 ? "badge-available" : "badge-sold"}`} data-testid={`card-available-${r.sku}`}>{r.available}</span><p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">Available</p></div>
                <div><p className="text-sm text-warmgrey">{r.sample_available}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Sample pool</p></div>
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
                <span className="text-[11px] text-muted-foreground">{r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}</span>
                {readOnly ? <span className="text-xs text-muted-foreground">Moves on receipt</span> : location === "MOROCCO" ? (
                  <Button variant="outline" size="sm" onClick={() => onSetMorocco(r)} data-testid={`card-set-morocco-${r.sku}`} className="border-limestone text-charcoal hover:bg-sand">Set stock</Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => onAdjust(r)} data-testid={`card-adjust-${r.sku}`} className="border-limestone text-charcoal hover:bg-sand">
                    <SlidersHorizontal className="mr-1 h-3.5 w-3.5" /> Adjust
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      {/* Desktop: full table — V5.1 location border colors (Morocco neutral, In Transit terracotta, Florida sage) */}
      <div className="hidden overflow-x-auto rounded-md border-2 bg-white shadow-[0_2px_12px_rgba(26,26,24,0.04)] md:block" style={{ borderColor: LOCATION_BORDER[location] }} data-testid={`inventory-location-card-${location.toLowerCase()}`}>
      <p className="px-4 pt-3 text-xs font-semibold uppercase tracking-widest text-charcoal" data-testid={`inventory-location-label-${location.toLowerCase()}`}>{LOCATION_LABEL[location]}</p>
      <Table data-testid={`inventory-table-${location.toLowerCase()}`} className="min-w-[1000px]">
        <TableHeader>
          <TableRow className="border-border">
            <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Variant</TableHead>
            <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Physical</TableHead>
            <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Reserved</TableHead>
            <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Committed</TableHead>
            <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Available</TableHead>
            <TableHead className="text-right text-xs uppercase tracking-widest text-muted-foreground">Sample pool</TableHead>
            <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Last updated</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={8} className="py-12 text-center text-sm text-muted-foreground" data-testid={`inventory-empty-${location.toLowerCase()}`}>No variants yet.</TableCell></TableRow>
          ) : (
            rows.map((r) => (
              <TableRow key={r.variant_id} className="border-border" data-testid={`inventory-row-${r.sku}`}>
                <TableCell className="py-4">
                  <p className="font-medium text-charcoal">{r.label}<UsName r={r} /></p>
                  <p className="font-mono text-xs text-muted-foreground">{r.sku} · {r.color_code ? `${r.color_code} · ` : ""}{r.box_coverage_sqft} sqft/box</p>
                </TableCell>
                <TableCell className="py-4 text-right text-sm text-charcoal">{r.physical}</TableCell>
                <TableCell className="py-4 text-right text-sm text-[#8a6435]">{r.reserved}</TableCell>
                <TableCell className="py-4 text-right text-sm text-status-sold">{r.committed}</TableCell>
                <TableCell className="py-4 text-right">
                  <span className={`badge-status ${r.available > 0 ? "badge-available" : "badge-sold"}`} data-testid={`available-${r.sku}`}>
                    {r.available}
                  </span>
                  <span className="num mt-1 block text-[11px] text-muted-foreground">{(Math.max(0, r.available * (r.box_coverage_sqft || 0))).toLocaleString(undefined, { maximumFractionDigits: 0 })} sq ft</span>
                  {(r.damaged > 0 || r.quarantined > 0) && <span className="mt-1 block text-[11px] text-status-reserved" data-testid={`held-${r.sku}`}>{r.damaged} damaged · {r.quarantined} quarantined</span>}
                </TableCell>
                <TableCell className="py-4 text-right text-sm text-warmgrey">{r.sample_available}</TableCell>
                <TableCell className="py-4 text-xs text-muted-foreground">
                  {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}
                </TableCell>
                <TableCell className="py-4 text-right">
                  {readOnly ? <span className="text-xs text-muted-foreground">Moves on receipt</span> : location === "MOROCCO" ? (
                    <Button variant="outline" size="sm" onClick={() => onSetMorocco(r)} data-testid={`set-morocco-${r.sku}`} className="border-limestone text-charcoal hover:bg-sand">
                      Set stock
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => onAdjust(r)} data-testid={`adjust-${r.sku}`} className="border-limestone text-charcoal hover:bg-sand">
                      <SlidersHorizontal className="mr-1 h-3.5 w-3.5" /> Adjust
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      </div>
    </>
  );
}

export default function Inventory() {
  const [location, setLocation] = useState("MOROCCO");
  const [rows, setRows] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ field: "physical", qty_delta: "", reason: "WAREHOUSE_CORRECTION", notes: "", lot_id: "" });
  const [lots, setLots] = useState([]);
  const lotField = location === "FLORIDA" && ["damaged", "quarantined"].includes(adjustForm.field);
  useEffect(() => {
    if (adjustTarget && location === "FLORIDA") {
      api.get(`/admin/inventory/lots?variant_id=${adjustTarget.variant_id}`).then(({ data }) => setLots(data)).catch(() => setLots([]));
    } else { setLots([]); }
  }, [adjustTarget, location]);
  const [moroccoTarget, setMoroccoTarget] = useState(null);
  const [moroccoValue, setMoroccoValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const [search, setSearch] = useState("");
  const [formatFilter, setFormatFilter] = useState("all");
  const fmts = useFormats();
  const [availFilter, setAvailFilter] = useState("all");
  const [minSqft, setMinSqft] = useState("");
  const [bpp, setBpp] = useState(null);  // boxes-per-pallet by format, from pricing config (§14)

  const load = () => {
    if (location === "AUDIT") return;
    setLoading(true);
    api
      .get(`/inventory?location=${location}${showLegacy ? "" : "&canonical=1"}`)
      .then((res) => setRows(location === "FLORIDA" ? res.data.map((r) => ({ ...r, available: r.atp })) : res.data))  // ID-055: damaged/quarantined excluded
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  const loadAudit = () => {
    api
      .get("/inventory/adjustments/feed?limit=100")
      .then((res) => setAudit(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  useEffect(load, [location, showLegacy]);
  useEffect(loadAudit, []);

  const handleAdjust = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/inventory/adjust", {
        variant_id: adjustTarget.variant_id,
        location_code: location,
        pool: adjustForm.field.startsWith("sample") ? "sample" : "sellable",
        field: adjustForm.field,
        qty_delta: parseInt(adjustForm.qty_delta, 10),
        reason: adjustForm.reason,
        notes: adjustForm.notes,
        lot_id: lotField && adjustForm.lot_id ? adjustForm.lot_id : null,
      });
      toast.success(`Adjusted: ${data.old_qty} → ${data.new_qty}`);
      setAdjustTarget(null);
      setAdjustForm({ field: "physical", qty_delta: "", reason: "WAREHOUSE_CORRECTION", notes: "", lot_id: "" });
      load();
      loadAudit();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleSetMorocco = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/inventory/morocco/set", {
        items: [{ variant_id: moroccoTarget.variant_id, physical: parseInt(moroccoValue, 10) }],
        notes: "Admin stock update",
      });
      toast.success("Morocco stock updated");
      setMoroccoTarget(null);
      setMoroccoValue("");
      load();
      loadAudit();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const currentValue = adjustTarget ? adjustTarget[adjustForm.field] ?? 0 : 0;
  const delta = parseInt(adjustForm.qty_delta, 10) || 0;

  // Operational search/filter (§B): client-side over the already-loaded rows —
  // inventory accounting model untouched.
  const q = search.trim().toUpperCase();
  const minSq = parseFloat(minSqft);
  const filteredRows = rows.filter((r) => {
    if (formatFilter !== "all" && (r.format_in || "") !== formatFilter) return false;
    if (availFilter === "available" && r.available <= 0) return false;
    if (availFilter === "unavailable" && r.available > 0) return false;
    // §14: full-pallet availability uses packaging config, never a hardcoded threshold
    if (availFilter === "full_pallet" && (r.available < ((bpp || {})[r.format_in] ?? Infinity))) return false;
    if (minSqft && !Number.isNaN(minSq) && r.available * (r.box_coverage_sqft || 0) < minSq) return false;
    if (!q) return true;
    const hay = `${r.search_text || ""} ${(r.sku || "")} ${(r.label || "")} ${(r.format_in || "")}`.toLowerCase().replace(/×/g, "x");
    return q.toLowerCase().replace(/×/g, "x").split(/\s+/).every((tok) => hay.includes(tok));
  });
  const auditRows = audit.filter((a) => !q || q.toLowerCase().split(/\s+/).every((tok) => `${a.variant_label || ""} ${a.sku || ""} ${a.container_ref || ""} ${a.reason || ""} ${(a.identity?.colors || []).map((c) => `${c.code} ${c.moroccan_name} ${c.us_name || ""}`).join(" ")} ${a.identity?.collection || ""} ${a.identity?.kind === "MOSAIC" ? "mosaic" : ""}`.toLowerCase().includes(tok)));

  return (
    <div data-testid="admin-inventory-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Stock</p>
      <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Inventory</h1>
      {location !== "AUDIT" && (
        <label className="mt-4 flex items-center gap-2 text-xs font-medium text-warmgrey" data-testid="legacy-toggle-row">
          <Switch checked={showLegacy} onCheckedChange={setShowLegacy} data-testid="show-legacy-toggle" />
          Show legacy / demo records ({showLegacy ? "including non-catalog history" : "canonical MZ catalog only"})
        </label>
      )}
      {location === "AUDIT" && (
        <div className="mt-3" data-testid="audit-controls">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search collection, SKU, color, container or reason…" data-testid="audit-search" className="h-11 w-full bg-white focus-visible:ring-terracotta sm:w-96" />
        </div>
      )}
      {location !== "AUDIT" && (
        <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="inventory-controls">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search collection, mosaic, SKU, color or format…" data-testid="inventory-search" className="h-11 w-full bg-white focus-visible:ring-terracotta sm:w-80" />
          <Select value={formatFilter} onValueChange={setFormatFilter}>
            <SelectTrigger data-testid="inventory-format-filter" className="h-11 w-36 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All formats</SelectItem>
              {fmts.map((f) => <SelectItem key={f.code} value={f.code}>{f.code.replace("x", "×")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={availFilter} onValueChange={setAvailFilter}>
            <SelectTrigger data-testid="inventory-availability-filter" className="h-11 w-52 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All availability</SelectItem>
              <SelectItem value="available">In stock (&gt; 0)</SelectItem>
              <SelectItem value="full_pallet">Full pallet available</SelectItem>
              <SelectItem value="unavailable">Unavailable</SelectItem>
            </SelectContent>
          </Select>
          <Input type="number" min="0" value={minSqft} onChange={(e) => setMinSqft(e.target.value)} placeholder="Min sq ft…" data-testid="inventory-min-sqft" className="h-11 w-32 bg-white focus-visible:ring-terracotta" />
          <HelpTip testid="help-inventory-quantities" text="Physical = boxes in the warehouse. Reserved = inventory temporarily held for a reservation. Committed = inventory allocated to an actual order. Available = physical − reserved − committed." />
        </div>
      )}

      <Tabs value={location === "AUDIT" ? "AUDIT" : location} onValueChange={(v) => (v === "AUDIT" ? setLocation("AUDIT") : setLocation(v))} className="mt-10">
        <TabsList className="bg-sand">
          <TabsTrigger value="MOROCCO" data-testid="tab-morocco">Morocco</TabsTrigger>
          <TabsTrigger value="IN_TRANSIT" data-testid="tab-in-transit">In Transit</TabsTrigger>
          <TabsTrigger value="FLORIDA" data-testid="tab-florida">Florida</TabsTrigger>
          <TabsTrigger value="AUDIT" data-testid="tab-audit">Audit trail</TabsTrigger>
        </TabsList>

        <TabsContent value="MOROCCO" className="mt-6">
          {loading ? <Loader2 className="h-6 w-6 animate-spin text-terracotta" /> : (
            <InventoryTable rows={filteredRows} location="MOROCCO" onAdjust={() => {}} onSetMorocco={(r) => { setMoroccoTarget(r); setMoroccoValue(String(r.physical)); }} />
          )}
        </TabsContent>
        <TabsContent value="IN_TRANSIT" className="mt-6">
          {loading ? <Loader2 className="h-6 w-6 animate-spin text-terracotta" /> : (
            <InventoryTable rows={filteredRows} location="IN_TRANSIT" onAdjust={() => {}} onSetMorocco={() => {}} />
          )}
        </TabsContent>
        <TabsContent value="FLORIDA" className="mt-6">
          {loading ? <Loader2 className="h-6 w-6 animate-spin text-terracotta" /> : (
            <InventoryTable rows={filteredRows} location="FLORIDA" onAdjust={(r) => setAdjustTarget(r)} onSetMorocco={() => {}} />
          )}
        </TabsContent>
        <TabsContent value="AUDIT" className="mt-6">
          {/* Mobile: complete audit event per card — no horizontal scroll */}
          <div className="space-y-3 md:hidden" data-testid="audit-cards">
            {audit.length === 0 ? (
              <p className="rounded-md border border-border bg-white py-12 text-center text-sm text-muted-foreground" data-testid="audit-cards-empty">No adjustments recorded yet.</p>
            ) : (
              auditRows.map((a) => (
                <div key={a.id} className="rounded-md border border-border bg-white p-4 shadow-[0_2px_12px_rgba(26,26,24,0.04)]" data-testid={`audit-card-${a.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-charcoal">{a.variant_label}{a.container_ref && <span className="block text-[11px] text-warmgrey">{a.container_ref}</span>}</p>
                    <span className="badge-plain">{REASON_LABELS[a.reason] || a.reason}</span>
                  </div>
                  <p className="mt-2 font-mono text-sm text-charcoal" data-testid={`audit-card-change-${a.id}`}>
                    {a.old_qty} → {a.new_qty} <span className={a.qty_delta >= 0 ? "text-status-available" : "text-status-sold"}>({a.qty_delta >= 0 ? "+" : ""}{a.qty_delta})</span>
                  </p>
                  <p className="mt-1 text-xs text-warmgrey">{a.location_code} · {a.pool === "sample" ? "Sample" : "Sellable"} / {{physical: "Physical", reserved: "Reserved", committed: "Committed", sample_physical: "Physical", sample_reserved: "Reserved"}[a.field] || a.field}</p>
                  <p className="mt-2 border-t border-border pt-2 text-[11px] text-muted-foreground">{new Date(a.created_at).toLocaleString()} · {a.user_name}</p>
                  {a.notes && <p className="mt-1 text-xs text-muted-foreground">{a.notes}</p>}
                </div>
              ))
            )}
          </div>
          {/* Desktop: full table */}
          <div className="hidden overflow-x-auto rounded-md border border-border bg-white shadow-[0_2px_12px_rgba(26,26,24,0.04)] md:block">
            <Table data-testid="audit-table" className="min-w-[1000px]">
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">When</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Variant</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Location</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Field</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Old → New</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Reason</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">By</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-muted-foreground">Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audit.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="py-12 text-center text-sm text-muted-foreground" data-testid="audit-empty">No adjustments recorded yet.</TableCell></TableRow>
                ) : (
                  auditRows.map((a) => (
                    <TableRow key={a.id} className="border-border" data-testid={`audit-row-${a.id}`}>
                      <TableCell className="py-3 text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString()}</TableCell>
                      <TableCell className="py-3 text-sm text-charcoal" data-testid={`audit-label-${a.id}`}>{a.variant_label}{a.container_ref && <span className="block text-[11px] text-warmgrey">{a.container_ref}</span>}</TableCell>
                      <TableCell className="py-3 text-xs text-warmgrey">{a.location_code}</TableCell>
                      <TableCell className="py-3 text-xs text-warmgrey">{a.pool === "sample" ? "Sample" : "Sellable"} / {{physical: "Physical", reserved: "Reserved", committed: "Committed", sample_physical: "Physical", sample_reserved: "Reserved"}[a.field] || a.field}</TableCell>
                      <TableCell className="py-3 font-mono text-xs text-charcoal">
                        {a.old_qty} → {a.new_qty} <span className={a.qty_delta >= 0 ? "text-status-available" : "text-status-sold"}>({a.qty_delta >= 0 ? "+" : ""}{a.qty_delta})</span>
                      </TableCell>
                      <TableCell className="py-3"><span className="badge-plain">{REASON_LABELS[a.reason] || a.reason}</span></TableCell>
                      <TableCell className="py-3 text-xs text-warmgrey">{a.user_name}</TableCell>
                      <TableCell className="max-w-[240px] truncate py-3 text-xs text-muted-foreground">{a.notes}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!adjustTarget} onOpenChange={(open) => !open && setAdjustTarget(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Adjust inventory</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">{adjustTarget?.label} — every adjustment is audited.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdjust} className="mt-2 space-y-5" data-testid="adjust-form">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Field</Label>
                <Select value={adjustForm.field} onValueChange={(v) => setAdjustForm((f) => ({ ...f, field: v }))}>
                  <SelectTrigger data-testid="adjust-field-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {Object.entries(FIELD_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Delta (+/−)</Label>
                <Input required type="number" data-testid="adjust-delta-input" value={adjustForm.qty_delta} onChange={(e) => setAdjustForm((f) => ({ ...f, qty_delta: e.target.value }))} className="h-11 bg-white focus-visible:ring-terracotta" />
              </div>
            </div>
            {lotField && lots.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Originating container lot</Label>
                <select required value={adjustForm.lot_id} onChange={(e) => setAdjustForm((f) => ({ ...f, lot_id: e.target.value }))} className="h-11 w-full rounded-md border border-border bg-white px-3 text-sm" data-testid="adjust-lot-select">
                  <option value="">Choose lot…</option>
                  {lots.map((l) => <option key={l.id} value={l.id}>{l.container_number} · {l.sku} · available {l.remaining_boxes} · damaged {l.damaged} · quarantined {l.quarantined}</option>)}
                </select>
              </div>
            )}
            <p className="text-sm text-warmgrey" data-testid="adjust-preview">
              {currentValue} → <span className="font-semibold text-charcoal">{Math.max(0, currentValue + delta)}</span>
            </p>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Reason</Label>
              <Select value={adjustForm.reason} onValueChange={(v) => setAdjustForm((f) => ({ ...f, reason: v }))}>
                <SelectTrigger data-testid="adjust-reason-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {REASONS.map((r) => <SelectItem key={r} value={r}>{REASON_LABELS[r] || r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Notes</Label>
              <Textarea data-testid="adjust-notes-input" value={adjustForm.notes} onChange={(e) => setAdjustForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className="bg-white focus-visible:ring-terracotta" />
            </div>
            <Button type="submit" disabled={saving} data-testid="adjust-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply adjustment"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!moroccoTarget} onOpenChange={(open) => !open && setMoroccoTarget(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">Set Morocco stock</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">{moroccoTarget?.label} — sets absolute physical count; audited.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSetMorocco} className="mt-2 space-y-5" data-testid="morocco-set-form">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Physical boxes</Label>
              <Input required type="number" min="0" data-testid="morocco-physical-input" value={moroccoValue} onChange={(e) => setMoroccoValue(e.target.value)} className="h-11 bg-white focus-visible:ring-terracotta" />
            </div>
            <Button type="submit" disabled={saving} data-testid="morocco-set-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set stock"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
